import { isEmail, isValidTelNumber } from '@actions/formValidation';
import Continue from '@components/Button/Continue';
import Checkbox from '@components/Checkbox';
import ChoiceGroup, { type ItemProps } from '@components/ChoiceGroup';
import Cell from '@components/ChoiceGroup/Cell';
import type { Choice } from '@components/ChoiceGroup/domain';
import Input from '@components/Input';
import InputPhone from '@components/InputPhone';
import MultiStepTitle from '@components/MultiStepTitle/MultiStepTitle';
import {
  getStepFields,
  type ChoiceFieldDef,
  type ConsentFieldDef,
  type FieldDefinition,
  type FlexInputFieldDef,
  type MultipleChoiceFieldDef,
  type StepDefinition,
  type StepKey,
} from '@config/formSteps';
import {
  etudesOptions,
  isRedirectionBranch,
  objectifOptions,
  REDIRECTION_URLS,
  UNCOVERED_DOMAIN_ID,
} from '@config/lists';
import { catalog } from '@core/catalog';
import {
  findFormation,
  getDefaultFormation,
  getDomainFormations,
} from '@core/theme';
import { type Validation } from '@ef-core/validation';
import { concatStyles } from '@styles/domain';
import { useEffect, useMemo, type FC } from 'react';
import { computeVisibleSteps } from '../../domain';
import * as styles from './StepElement.css';
import { Field, FlexField } from './StepElement.styled';

type Answers = Record<string, unknown>;

interface Props {
  stepData: StepDefinition;
  formDataContext: Answers;
  handleFormData: (data: { name: string; value: unknown }) => void;
  advanceTo: (key: StepKey) => void;
  next?: StepKey;
}

const LAYOUT: Record<'row' | 'grid' | 'gridPairs' | 'column', string> = {
  row: styles.layout.row,
  grid: styles.layout.grid,
  gridPairs: styles.layout.gridPairs,
  column: styles.layout.column,
};

const buildValidate =
  (inputType: 'email' | 'tel' | 'name') =>
  (value?: string): Validation => {
    if (!value) {
      return { valid: false, error: 'Ce champ est requis.' };
    }
    if (inputType === 'email') {
      return isEmail(value)
        ? { valid: true }
        : { valid: false, error: 'Adresse mail invalide.' };
    }
    if (inputType === 'tel') {
      return isValidTelNumber(value)
        ? { valid: true }
        : { valid: false, error: 'Téléphone invalide.' };
    }
    return { valid: true };
  };

const resolveLabel = (
  options: { id: string; name: string }[],
  id: unknown,
): string => options.find((opt) => opt.id === id)?.name ?? String(id ?? '');

const StepElement: FC<Props> = ({
  stepData,
  formDataContext,
  handleFormData,
  advanceTo,
  next,
}) => {
  const fields = useMemo(
    () =>
      getStepFields(stepData.key, {
        catalog: catalog(),
        answers: formDataContext,
        onChange: handleFormData,
      }),
    [stepData.key, formDataContext, handleFormData],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const redirectToForm = ({
    target,
    answers,
  }: {
    target: { value: string; origin: string };
    answers: Answers;
  }) => {
    const trainingId = answers.idFormationBitrix as number | undefined;
    const dataToForward = {
      formation:
        findFormation({ themes: catalog().themes, id: Number(trainingId) })
          ?.name ?? trainingId,
      objectif: resolveLabel(objectifOptions, answers.objectif),
      niveauEtudes: resolveLabel(etudesOptions, answers.etudes),
      idformationbitrix: trainingId,
    };

    const utmToForward = Object.fromEntries(
      Object.entries(catalog().tracking).filter(([name]) =>
        name.startsWith('utm_'),
      ),
    );

    const updatedQueryParams = new URLSearchParams(window.location.search);

    const forwarded = {
      ...utmToForward,
      ...dataToForward,
      origine: target.origin,
    };

    Object.entries(forwarded).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        updatedQueryParams.set(key, String(value));
      }
    });

    const url = `${target.value}?${updatedQueryParams}`;
    window.location.assign(url);
  };

  // Champs supplémentaires dérivés d'une sélection (domaine → formation, etc.).
  const selectionPatch = (
    field: ChoiceFieldDef,
    choice: Choice<string>,
  ): Answers => {
    const themes = catalog().themes;
    if (field.name === 'domainId') {
      const domainId = Number(choice.value);
      if (domainId === UNCOVERED_DOMAIN_ID) {
        return { domainId, idFormationBitrix: null, formationId: null };
      }
      const formations = getDomainFormations({ themes, domainId });
      const single = formations.length === 1 ? formations[0] : null;
      return {
        domainId,
        idFormationBitrix: single?.id_formation_bitrix ?? null,
        formationId: single?.id ?? null,
        domainName: null,
      };
    }
    if (field.name === 'idFormationBitrix') {
      const domainId = Number(formDataContext.domainId);
      if (choice.value === 'nothing') {
        const fallback = getDefaultFormation({ themes, domainId });
        return {
          idFormationBitrix: fallback?.id_formation_bitrix ?? null,
          formationId: fallback?.id ?? null,
          hasRequestedAdvice: 1,
        };
      }
      const formation = getDomainFormations({ themes, domainId }).find(
        (f) => String(f.id_formation_bitrix) === choice.value,
      );
      return {
        idFormationBitrix: Number(choice.value),
        formationId: formation?.id ?? null,
        hasRequestedAdvice: 0,
      };
    }
    return { [field.name]: choice.value };
  };

  const advanceFrom = (patch: Answers) => {
    const patched = { ...formDataContext, ...patch };
    const steps = computeVisibleSteps({ answers: patched, catalog: catalog() });
    const nextKey = steps[steps.indexOf(stepData.key) + 1];
    if (nextKey) {
      advanceTo(nextKey);
    }
  };

  const handleSelect = (field: ChoiceFieldDef, value: string) => {
    const choice = field.choices.find((item) => item.value === value);
    if (!choice) {
      return;
    }
    const patch = selectionPatch(field, choice);
    Object.entries(patch).forEach(([name, next]) =>
      handleFormData({ name, value: next }),
    );

    if (choice.branch && isRedirectionBranch(choice.branch)) {
      return redirectToForm({
        target: REDIRECTION_URLS[choice.branch],
        answers: { ...formDataContext, ...patch },
      });
    }

    // Auto-avance : step mono-champ, sauf option « Précisez » (attend la saisie enfant).
    if (
      fields.length === 1 &&
      choice.type !== 'emojiInput' &&
      choice.type !== 'iconInput'
    ) {
      advanceFrom(patch);
    }
  };

  const checkField = (field: FieldDefinition): boolean => {
    switch (field.kind) {
      case 'choice': {
        const value = formDataContext[field.name];
        if (value === undefined || value === null || value === '') {
          return false;
        }
        const selected = field.choices.find((c) => c.value === String(value));
        if (
          selected &&
          (selected.type === 'emojiInput' || selected.type === 'iconInput')
        ) {
          return !!formDataContext[selected.input.name];
        }
        return true;
      }
      case 'multipleChoice': {
        const value = formDataContext[field.name];
        return Array.isArray(value) && value.length > 0;
      }
      case 'flex':
        return field.components.every((component) => {
          const value = formDataContext[component.name];
          if (component.inputType === 'email')
            return isEmail(String(value ?? ''));
          if (component.inputType === 'tel')
            return isValidTelNumber(value as string | undefined);
          return !!value;
        });
      case 'consent':
        return !field.required || formDataContext[field.name] === 'on';
    }
  };

  const disabled = !fields.length || !fields.every(checkField);

  const handleContinue = () => {
    if (next) {
      advanceTo(next);
    }
  };

  const renderCell = ({
    layout,
    choice,
    selected,
    itemProps,
  }: {
    layout: ChoiceFieldDef['layout'] | MultipleChoiceFieldDef['layout'];
    choice: Choice<string>;
    selected: boolean;
    itemProps: ItemProps;
  }) => (
    <Cell
      choice={choice}
      selected={selected}
      className={concatStyles([
        layout === 'row' && styles.rowCell,
        layout === 'gridPairs' &&
          choice.type === 'emojiInput' &&
          styles.fullRow,
      ])}
      {...itemProps}
    />
  );

  const renderChoice = (field: ChoiceFieldDef) => {
    const value = formDataContext[field.name];
    return (
      <ChoiceGroup<string>
        key={field.name}
        choices={field.choices}
        value={
          value === undefined || value === null ? undefined : String(value)
        }
        onSelect={(next) => handleSelect(field, next)}
        className={LAYOUT[field.layout ?? 'column']}
        renderChoice={(args) => renderCell({ layout: field.layout, ...args })}
      />
    );
  };

  const renderMultipleChoice = (field: MultipleChoiceFieldDef) => {
    const value = formDataContext[field.name];
    return (
      <ChoiceGroup<string>
        key={field.name}
        choices={field.choices}
        multiple
        values={Array.isArray(value) ? value.map(String) : []}
        standaloneValues={field.standaloneValues}
        onSelect={(values) =>
          handleFormData({
            name: field.name,
            value: values,
          })
        }
        className={LAYOUT[field.layout ?? 'column']}
        renderChoice={(args) => renderCell({ layout: field.layout, ...args })}
      />
    );
  };

  const renderFlex = (field: FlexInputFieldDef) => (
    <FlexField key={field.name}>
      {field.components.map((component) => {
        const value = (formDataContext[component.name] as string) || '';
        const onChange = (next: string) =>
          handleFormData({ name: component.name, value: next });
        return (
          <Field key={component.name}>
            {component.inputType === 'tel' ? (
              <InputPhone
                label={component.label}
                id={component.name}
                name={component.name}
                placeholder={component.placeholder}
                required
                value={value}
                onChange={onChange}
                validate={buildValidate('tel')}
              />
            ) : (
              <Input
                label={component.label}
                type={component.inputType === 'email' ? 'email' : 'text'}
                id={component.name}
                name={component.name}
                placeholder={component.placeholder}
                required
                value={value}
                onChange={onChange}
                validate={buildValidate(component.inputType)}
              />
            )}
          </Field>
        );
      })}
    </FlexField>
  );

  const renderConsent = (field: ConsentFieldDef) => (
    <Checkbox
      key={field.name}
      className='form-control'
      checked={formDataContext[field.name] === 'on'}
      onChange={(checked: boolean) =>
        handleFormData({ name: field.name, value: checked ? 'on' : 'off' })
      }
      validate={
        field.required
          ? (checked?: boolean) =>
              checked
                ? { valid: true }
                : { valid: false, error: 'Ce champ est requis.' }
          : undefined
      }
    >
      {field.label}
    </Checkbox>
  );

  const renderField = (field: FieldDefinition) => {
    switch (field.kind) {
      case 'choice':
        return renderChoice(field);
      case 'multipleChoice':
        return renderMultipleChoice(field);
      case 'flex':
        return renderFlex(field);
      case 'consent':
        return renderConsent(field);
    }
  };

  return (
    <>
      <MultiStepTitle title={stepData.title} subtitle={stepData.subtext} />
      {fields.map(renderField)}
      <Continue
        id='continue'
        label={!next ? 'Obtenir des informations' : 'Continuer'}
        disabled={disabled}
        loading={false}
        className={undefined}
        type={!next ? 'submit' : 'button'}
        onClickHandler={handleContinue}
      >
        {null}
      </Continue>
    </>
  );
};

export default StepElement;
